import { BhapticsContent } from "@/store/reducers/contents";
import { useTrans } from "@/utils/useTrans";
import DonutProgress from "@/v2-components/pages/games/detail/DonutProgress";
import ReviewAndOpinionDialog from "@/v2-components/pages/games/detail/ReviewAndOpinionDialog";
import { Trans } from "next-i18next";
import React from "react";

interface Props {
  game: BhapticsContent;
}

export default function HapticFeedbackReview({ game }: Props) {
  const { t } = useTrans(["games"]);
  const ratingPoint = Math.ceil(game.avgRatingPoint);

  return (
    <div>
      {/**
       * 시안 `29889:101500` — 섹션 내부 갭은 **20**(`gap-[20px]`)로 두 폭 모두 같다.
       * 종전 `laptop:mb-8`(32)은 형제 섹션(`ReviewVideos` mb-5)과도 어긋났다.
       *
       * ★**색이 폭마다 다르다** — 데스크탑 `30035:21977` `#121619`(cg-100) ·
       * 모바일은 상속값 `#21272a`(cg-90). 종전엔 색 지정이 없어 두 폭 다 cg-90 이었다.
       *
       * ⚠️ **모든 섹션 제목이 그런 것은 아니다.** 세 자리를 떠서 대조했다 —
       * 「햅틱 평가」·「리뷰 영상」은 데스크탑 cg-100 인데 **「비슷한 게임」
       * (`30035:22067`)만 cg-90** 이다. 한 자리를 보고 전부에 미는 것을 막으려고 적어 둔다.
       */}
      <h2 className="mb-5 text-2xl laptop:text-3xl laptop:text-coolgray-100">
        {t(`Haptic Feedback Review`) +
          (game.reviewCount !== 0 ? ` (${game.reviewCount})` : "")}
      </h2>

      {game.reviewCount === 0 && (
        <div className="flex w-full flex-col items-center justify-center rounded-lg bg-coolgray-10 py-6 laptop:rounded-2xl laptop:py-12">
          <p className="mb-3 text-lg font-500">{t(`No Review Yet`)}</p>
          <p className="mb-6 text-base text-coolgray-70">
            {t(`Be the first reviewer!`)}
          </p>
          <ReviewAndOpinionDialog
            reviewCount={game.reviewCount}
            view="rate"
            contentId={game.id}
            slug={game.slug}
            contentName={game.name}
          />
        </div>
      )}
      {game.reviewCount > 0 && (
        /**
         * 시안 `29889:101502`(`rate`) — 게이지와 Detail 이 **세로로 쌓인다**.
         *
         * ★종전엔 `laptop:grid-cols-2` 라 좌우로 갈라져 각 464 였다. 시안은 게이지 묶음
         * (`101503`, 952×266)이 위, Detail(`101522`, 952×248)이 아래다.
         * 가로 반쪽에 도넛 3개를 넣으면 게이지가 작아지고 Recommendation 문구가 3줄로 접힌다.
         */
        <div className="flex flex-col gap-5 laptop:gap-6">
          <DesktopProgress game={game} />
          <MobileProgress game={game} />
          {/**
           * 시안 데스크탑 `29889:101522` · 모바일 `30035:21488` — `#f2f4f7` · 컨테이너 갭 24.
           *
           * ## ★간격을 `mb-*` 와 컨테이너 `gap` 으로 **이중으로** 주고 있었다
           *
           * 이 상자는 `flex-col gap-6`(24)인데 자식마다 `mb-3`·`mb-6` 이 또 붙어 있어
           * 실제 간격이 **더해졌다**(실측 2026-09-03 · 360):
           *
           * ```
           *                  시안   종전 실측        원인
           *   제목 → 문구      12    36            gap 24 + mb-3 12
           *   문구 → 게이지    24    48            gap 24 + mb-6 24
           *   게이지 → 라벨행   8     8   ✅        mt-2
           * ```
           *
           * 시안은 **제목+문구가 한 블록**(내부 갭 12)이고 그 블록과 게이지 사이가 24다.
           * 그래서 `mb-*` 를 걷고 제목·문구를 `gap-3` 블록으로 묶었다 — 이러면 컨테이너
           * `gap-6` 하나로 24 가 두 자리(블록↔게이지 · 게이지↔버튼) 다 맞는다.
           *
           * ⚠️ 좌우 패딩이 폭마다 다르다: 모바일 `px16 py24`(`30035:21488`) ·
           * 데스크탑 `p24`(`29889:101522` Body 가 x=24). 세로는 둘 다 24다.
           */}
          <div className="flex h-full w-full flex-col items-center justify-center gap-6 rounded-lg bg-coolgray-10 px-4 py-6 laptop:rounded-2xl laptop:px-6">
            {ratingPoint >= 70 ? (
              <>
                {/* `30035:21490` — 제목↔문구 12. */}
                <div className="flex flex-col items-center gap-3">
                  {/* `21491` 16/22 Medium `#21272a` tr .15 */}
                  <p className="text-lg font-500 tracking-[0.15px]">
                    {t("Recommendation")}
                  </p>
                  {/* `21492` 14/18 `#4d5358` tr .25 — 퍼센트만 `#0f62fe`. */}
                  <p className="whitespace-pre-wrap text-center text-base tracking-[0.25px] text-coolgray-70">
                    <Trans
                      t={t}
                      i18nKey={`Over <strong>{{ratingPoint}}%</strong> of players responded<br/>that haptics enhanced<br/>their gaming experience.`}
                      components={{
                        /**
                         * ★**강조를 되살렸다.** 종전 주석은 *"앱은 강조가 없는 디자인"*
                         * 이라며 무장식 `span` 을 썼는데, 개정 시안 `30035:21492` 는
                         * 퍼센트 조각만 `#0f62fe`(blue-60)로 칠한다. 웹뷰 사본이 같은 키를
                         * 노란 강조로 매핑하는 것과는 별개다(파일이 다르다).
                         *
                         * ⚠️ 매핑 자체를 빼면 안 된다 — Trans 가 태그를 못 풀어 화면에
                         * `<strong>` 이 글자로 나온다.
                         */
                        strong: <span className="text-blue-60" />,
                        br: <br />,
                      }}
                      values={{ ratingPoint }}
                    />
                  </p>
                </div>

                {/* `30035:21493` — 바↔라벨행 8. */}
                <div className="flex w-full flex-col gap-2">
                  <div className="h-3 w-full rounded-full bg-blue-30">
                    <div
                      className="h-full rounded-full bg-blue-60"
                      style={{
                        width: `${ratingPoint}%`,
                      }}
                    />
                  </div>
                  {/**
                   * ★좌우가 **같은 스타일이 아니다**(`21499` vs `21501`):
                   * 왼쪽 12/16 SemiBold `#21272a` · 오른쪽 12/16 Regular `#4d5358`.
                   * 긍정 쪽을 강조하는 위계라 상속으로 두면 둘 다 진해진다.
                   */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-sm font-600 tracking-[0.4px] text-coolgray-90">
                      <HeartEmoji />
                      <span>{`${ratingPoint}%`}</span>
                    </div>
                    <div className="flex items-center gap-1 text-sm tracking-[0.4px] text-coolgray-70">
                      <span>{`${100 - ratingPoint}%`}</span>
                      <SadEmoji />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <p className="whitespace-pre-wrap text-center text-base text-coolgray-70">
                {t("Please share your experience with TactSuit.")}
              </p>
            )}

            <ReviewAndOpinionDialog
              contentName={game.name}
              reviewCount={game.reviewCount}
              view="rate"
              slug={game.slug}
              contentId={game.id}
            />
          </div>
        </div>
      )}
    </div>
  );
}

interface ProgressProps {
  game: BhapticsContent;
}
function DesktopProgress({ game }: ProgressProps) {
  const { t } = useTrans(["games"]);

  return (
    <div className="hidden items-start justify-around laptop:flex">
      <div>
        <div className="relative">
          <DonutProgress
            score={game.avgHapticsSatisfactionRating}
            className="stroke-bhaptics-yellow-hover"
          />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-3xl">
            {ceilRating(game.avgHapticsSatisfactionRating)}
          </div>
        </div>
        <p className="mt-5 whitespace-pre-wrap text-center text-base tracking-[0.25px] text-coolgray-70">
          <Trans
            t={t}
            i18nKey={`Haptic<br/>Satisfaction`}
            components={{ br: <br /> }}
          />
        </p>
      </div>
      <div>
        <div className="relative">
          <DonutProgress
            score={game.avgHapticsQualityRating}
            className="stroke-blue-60"
          />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-3xl">
            {ceilRating(game.avgHapticsQualityRating)}
          </div>
        </div>
        <p className="mt-5 whitespace-pre-wrap text-center text-base tracking-[0.25px] text-coolgray-70">
          <Trans
            t={t}
            i18nKey={`Haptic<br/>Quality`}
            components={{ br: <br /> }}
          />
        </p>
      </div>
      <div>
        <div className="relative">
          <DonutProgress
            score={game.avgConnectionConvenienceRating}
            className="stroke-green-40"
          />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-3xl">
            {ceilRating(game.avgConnectionConvenienceRating)}
          </div>
        </div>
        <p className="mt-5 whitespace-pre-wrap text-center text-base tracking-[0.25px] text-coolgray-70">
          <Trans
            t={t}
            i18nKey={`Game<br/>Connection<br/>Convenience`}
            components={{ br: <br /> }}
          />
        </p>
      </div>
    </div>
  );
}
function MobileProgress({ game }: ProgressProps) {
  const { t } = useTrans(["games"]);
  return (
    <div className="flex items-start justify-around laptop:hidden">
      <div>
        <div className="relative">
          <DonutProgress
            radius={45}
            stroke={7}
            score={game.avgHapticsSatisfactionRating}
            className="stroke-bhaptics-yellow-hover"
          />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-3xl">
            {ceilRating(game.avgHapticsSatisfactionRating)}
          </div>
        </div>
        <p className="mt-3 whitespace-pre-wrap text-center text-base tracking-[0.25px] text-coolgray-70">
          <Trans
            t={t}
            i18nKey={`Haptic<br/>Satisfaction`}
            components={{ br: <br /> }}
          />
        </p>
      </div>
      <div>
        <div className="relative">
          <DonutProgress
            radius={45}
            stroke={7}
            score={game.avgHapticsQualityRating}
            className="stroke-blue-60"
          />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-3xl">
            {ceilRating(game.avgHapticsQualityRating)}
          </div>
        </div>
        <p className="mt-3 whitespace-pre-wrap text-center text-base tracking-[0.25px] text-coolgray-70">
          <Trans
            t={t}
            i18nKey={`Haptic<br/>Quality`}
            components={{ br: <br /> }}
          />
        </p>
      </div>
      <div>
        <div className="relative">
          <DonutProgress
            radius={45}
            stroke={7}
            score={game.avgConnectionConvenienceRating}
            className="stroke-green-40"
          />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-3xl">
            {ceilRating(game.avgConnectionConvenienceRating)}
          </div>
        </div>
        <p className="mt-3 whitespace-pre-wrap text-center text-base tracking-[0.25px] text-coolgray-70">
          <Trans
            t={t}
            i18nKey={`Game<br/>Connection<br/>Convenience`}
            components={{ br: <br /> }}
          />
        </p>
      </div>
    </div>
  );
}

const ceilRating = (rating: number) => {
  return Math.ceil(rating * 10) / 10;
};

function HeartEmoji() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.9425 18.849C6.94645 18.849 2.63525 16.3424 2.63525 10.8424C2.63525 5.34245 6.94645 2.83594 10.9425 2.83594C13.1626 2.83594 15.2108 3.56641 16.729 4.89844C18.3761 6.35938 19.2498 8.42188 19.2498 10.8424C19.2498 13.263 18.3761 15.3112 16.729 16.7721C15.2108 18.1042 13.1483 18.849 10.9425 18.849Z"
        fill="url(#paint0_radial_15079_74030)"
      />
      <path
        d="M17.8019 6.08203C18.5653 7.3138 18.9634 8.78047 18.9634 10.4118C18.9634 12.8324 18.0897 14.8806 16.4426 16.3415C14.9244 17.6736 12.8619 18.4184 10.6562 18.4184C8.06943 18.4184 5.35667 17.3656 3.75537 15.1327C5.29652 17.6621 8.19548 18.848 10.9426 18.848C13.1483 18.848 15.2108 18.1033 16.7291 16.7712C18.3762 15.3103 19.2499 13.2621 19.2499 10.8415C19.2499 9.0168 18.7529 7.39544 17.8019 6.08203Z"
        fill="#EB8F00"
      />
      <path
        d="M11.0575 13.7637C10.5447 13.7637 10.0362 13.6777 9.55356 13.5059C8.95916 13.301 8.3132 13.6176 8.10838 14.2105C7.99236 14.5457 8.03963 14.9152 8.23585 15.2103C8.85174 16.1556 9.8257 16.6712 11.0575 16.6712C12.2892 16.6712 13.2632 16.1556 13.8791 15.2103C14.2271 14.6875 14.0839 13.9814 13.5611 13.6333C13.4156 13.5365 13.2496 13.4746 13.0762 13.4525C12.9028 13.4304 12.7266 13.4486 12.5614 13.5059C12.0787 13.6777 11.5702 13.7637 11.0575 13.7637Z"
        fill="#422B0D"
      />
      <path
        opacity="0.8"
        d="M6.73161 14.6094C8.11592 14.6094 9.23812 13.5513 9.23812 12.2461C9.23812 10.9409 8.11592 9.88281 6.73161 9.88281C5.3473 9.88281 4.2251 10.9409 4.2251 12.2461C4.2251 13.5513 5.3473 14.6094 6.73161 14.6094Z"
        fill="url(#paint1_radial_15079_74030)"
      />
      <path
        opacity="0.8"
        d="M15.383 14.6094C16.7673 14.6094 17.8895 13.5513 17.8895 12.2461C17.8895 10.9409 16.7673 9.88281 15.383 9.88281C13.9987 9.88281 12.8765 10.9409 12.8765 12.2461C12.8765 13.5513 13.9987 14.6094 15.383 14.6094Z"
        fill="url(#paint2_radial_15079_74030)"
      />
      <path
        d="M17.2432 6.75602C16.0672 6.42516 15.2036 7.38623 15.2036 7.38623C15.2036 7.38623 15.1033 6.29625 14.0978 5.95394C12.8918 5.54 11.6185 6.19886 11.5426 7.90758C11.4567 9.85407 14.032 12.4093 14.032 12.4093C14.032 12.4093 17.2288 11.5499 18.2758 9.72802C19.128 8.2456 18.1326 7.00524 17.2432 6.75602Z"
        fill="#F44336"
      />
      <path
        d="M17.2432 6.75547C17.0989 6.71711 16.9499 6.69927 16.8006 6.70247C17.2215 6.98407 17.5549 7.37807 17.7631 7.83971C17.965 8.32526 18.018 8.9526 17.6342 9.70169C16.802 11.3302 14.2396 12.3385 14.0535 12.4087C14.2869 12.3428 17.2747 11.4835 18.2816 9.73177C19.1281 8.24505 18.1326 7.00469 17.2432 6.75547ZM15.2021 7.56185C15.1936 7.41289 15.1706 7.26393 15.1363 7.11784C15.0951 6.9094 15.0099 6.71216 14.8864 6.53927C14.7629 6.36637 14.6039 6.22181 14.4201 6.11523C14.1652 5.96628 13.883 5.87174 13.5908 5.83594C13.5908 5.83594 14.0205 5.96628 14.327 6.56068C14.4831 6.87292 14.5762 7.2138 14.5992 7.56185C14.5977 7.68932 14.6206 7.81393 14.6665 7.93281C14.718 8.05169 14.8441 8.12188 14.973 8.10182C15.1692 8.05169 15.2122 7.80677 15.2021 7.56185Z"
        fill="#C62828"
      />
      <path
        d="M12.4179 6.88013C12.6858 6.54784 13.1255 6.23131 13.5466 6.52349C13.77 6.67961 13.7729 7.12219 13.4993 7.32128C13.0381 7.6593 13.0166 7.88131 12.9651 8.0675C12.902 8.28951 12.8619 8.55448 12.66 8.6662C12.458 8.77792 12.2804 8.6662 12.1744 8.3468C11.994 7.84836 12.0856 7.29263 12.4179 6.88013Z"
        fill="#FF847A"
      />
      <path
        d="M7.66578 5.92455C6.50419 6.30125 6.31799 7.58028 6.31799 7.58028C6.31799 7.58028 5.63049 6.72091 4.59924 6.99877C3.3689 7.32247 2.67424 8.57716 3.55224 10.0395C4.56917 11.7153 8.12698 12.41 8.12698 12.41C8.12698 12.41 10.3112 9.92351 10.1737 7.82664C10.0606 6.11935 8.54378 5.63953 7.66578 5.92455Z"
        fill="#F44336"
      />
      <path
        d="M7.66562 5.92456C7.52383 5.9704 7.38776 6.03628 7.26315 6.11935C7.77018 6.12079 8.26575 6.26402 8.69544 6.53329C9.13372 6.81974 9.52474 7.31818 9.62214 8.15607C9.83268 9.97365 8.25716 12.2324 8.13542 12.3971C8.2944 12.2138 10.3039 9.84188 10.1721 7.82521C10.0604 6.11935 8.54362 5.63954 7.66562 5.92456ZM6.41667 7.73068C6.32786 7.60894 6.2276 7.49722 6.11589 7.39696C5.8151 7.09045 5.39831 6.92717 4.97005 6.95008C4.67643 6.96727 4.38854 7.04318 4.125 7.17639C4.125 7.17639 4.55469 7.04605 5.13906 7.37261C5.44414 7.54878 5.70911 7.78511 5.92109 8.06584C5.98984 8.17326 6.07865 8.26493 6.1832 8.33797C6.29206 8.40816 6.43529 8.39527 6.53125 8.3079C6.65872 8.15321 6.5599 7.92691 6.41667 7.73068Z"
        fill="#C62828"
      />
      <path
        d="M3.72378 8.70749C3.72951 8.58717 3.751 8.46829 3.7868 8.35371C3.84553 8.11309 4.01597 7.914 4.2437 7.81803C4.46285 7.74928 4.76649 7.79798 4.88394 8.01712C4.9885 8.21191 4.91258 8.44681 4.85529 8.66165C4.80373 8.90801 4.77508 9.16009 4.76936 9.41217C4.77012 9.51107 4.73166 9.60625 4.6624 9.67685C4.59314 9.74745 4.49872 9.78773 4.39982 9.78887C4.20503 9.78887 4.05321 9.61986 3.94436 9.45801C3.79255 9.23782 3.71525 8.9748 3.72378 8.70749Z"
        fill="#FF847A"
      />
      <defs>
        <radialGradient
          id="paint0_radial_15079_74030"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(10.9425 10.8424) scale(8.15833)"
        >
          <stop offset="0.5" stopColor="#FDE030" />
          <stop offset="0.92" stopColor="#F7C02B" />
          <stop offset="1" stopColor="#F4A223" />
        </radialGradient>
        <radialGradient
          id="paint1_radial_15079_74030"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(6.73162 11.1591) scale(2.73163 2.4529)"
        >
          <stop stopColor="#ED7770" />
          <stop offset="0.9" stopColor="#ED7770" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id="paint2_radial_15079_74030"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(19.4679 363.858) scale(2.78724)"
        >
          <stop stopColor="#ED7770" />
          <stop offset="0.9" stopColor="#ED7770" stopOpacity="0" />
        </radialGradient>
      </defs>
    </svg>
  );
}

function SadEmoji() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.9425 18.849C6.94645 18.849 2.63525 16.3424 2.63525 10.8424C2.63525 5.34245 6.94645 2.83594 10.9425 2.83594C13.1626 2.83594 15.2108 3.56641 16.729 4.89844C18.3761 6.35938 19.2498 8.42188 19.2498 10.8424C19.2498 13.263 18.3761 15.3112 16.729 16.7721C15.2108 18.1042 13.1483 18.849 10.9425 18.849Z"
        fill="url(#paint0_radial_15079_74034)"
      />
      <path
        d="M10.9998 16.1432C11.5512 16.1432 12.0983 16.2378 12.6182 16.4197C13.7211 16.8049 14.6764 15.5603 14.0362 14.5863C13.3688 13.5794 12.3304 13.293 10.9998 13.293C9.66915 13.293 8.63218 13.5665 7.96473 14.582C7.32306 15.5574 8.27983 16.7992 9.3827 16.4154C9.90262 16.2335 10.4498 16.1418 10.9998 16.1432Z"
        fill="#422B0D"
      />
      <path
        d="M17.8019 6.08203C18.5653 7.3138 18.9634 8.78047 18.9634 10.4118C18.9634 12.8324 18.0897 14.8806 16.4426 16.3415C14.9244 17.6736 12.8619 18.4184 10.6562 18.4184C8.06943 18.4184 5.35667 17.3656 3.75537 15.1327C5.29652 17.6621 8.19548 18.848 10.9426 18.848C13.1483 18.848 15.2108 18.1033 16.7291 16.7712C18.3762 15.3103 19.2499 13.2621 19.2499 10.8415C19.2499 9.0168 18.7529 7.39544 17.8019 6.08203Z"
        fill="#EB8F00"
      />
      <path
        d="M5.75625 7.53024C5.44115 7.5861 5.42539 8.04443 5.76914 8.06018C6.52826 8.07164 7.26159 7.79091 7.82018 7.27672C8.03258 7.09412 8.21228 6.87664 8.35156 6.63362C8.43464 6.51474 8.40599 6.35146 8.28854 6.26695C8.17109 6.18245 8.00638 6.21253 7.92188 6.32998L7.90755 6.3443C7.34466 6.97307 6.58698 7.38987 5.75625 7.53024ZM14.1681 6.3486L14.1538 6.33427C14.1137 6.27717 14.0525 6.23831 13.9838 6.22623C13.915 6.21414 13.8443 6.22982 13.7871 6.26982C13.7588 6.28967 13.7347 6.31494 13.7162 6.34415C13.6977 6.37336 13.6851 6.40595 13.6793 6.44003C13.6734 6.47412 13.6744 6.50902 13.682 6.54274C13.6897 6.57646 13.704 6.60832 13.7241 6.63649C13.8645 6.87854 14.0449 7.09482 14.2583 7.27672C14.8169 7.79091 15.5503 8.07021 16.3094 8.05732C16.6531 8.04156 16.6374 7.58323 16.3208 7.52737C15.4901 7.38844 14.7324 6.97307 14.1681 6.3486Z"
        fill="#422B0D"
      />
      <path
        d="M8.23128 9.70117C7.63115 9.70117 7.08545 10.2082 7.08545 11.0504C7.08545 11.8926 7.63115 12.3982 8.23128 12.3982C8.83141 12.3982 9.37712 11.8911 9.37712 11.0504C9.37712 10.2096 8.83141 9.70117 8.23128 9.70117Z"
        fill="#422B0D"
      />
      <path
        d="M8.17543 10.2665C8.07736 10.2198 7.96482 10.2138 7.86234 10.2498C7.75986 10.2857 7.67573 10.3607 7.6283 10.4584C7.55239 10.6174 7.58676 10.8079 7.71424 10.9311C7.91762 11.0285 8.16254 10.9425 8.26137 10.7391C8.33728 10.5802 8.30291 10.3897 8.17543 10.2665Z"
        fill="#896024"
      />
      <path
        d="M13.7215 9.70117C13.1214 9.70117 12.5757 10.2082 12.5757 11.0504C12.5757 11.8926 13.1214 12.3982 13.7215 12.3982C14.3216 12.3982 14.8674 11.8911 14.8674 11.0504C14.8674 10.2096 14.3245 9.70117 13.7215 9.70117Z"
        fill="#422B0D"
      />
      <path
        d="M13.6681 10.2665C13.57 10.2198 13.4575 10.2138 13.355 10.2498C13.2525 10.2857 13.1684 10.3607 13.121 10.4584C13.0451 10.6174 13.0794 10.8079 13.2069 10.9311C13.4103 11.0285 13.6552 10.9425 13.754 10.7391C13.83 10.5802 13.7956 10.3897 13.6681 10.2665Z"
        fill="#896024"
      />
      <defs>
        <radialGradient
          id="paint0_radial_15079_74034"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(10.9425 10.8424) scale(8.15833)"
        >
          <stop offset="0.5" stopColor="#FDE030" />
          <stop offset="0.92" stopColor="#F7C02B" />
          <stop offset="1" stopColor="#F4A223" />
        </radialGradient>
      </defs>
    </svg>
  );
}
